import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";

import DatagridTooltipProvider from "./datagridTooltipProvider.js";
import { useEffect, useMemo, useRef, useState } from "react";

import { List, Refresh } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import {
  createOmniPaginationObject,
  mrtGetActionColumnStyling,
  mrtGetActionIconSx,
  mrtGetBodyRowSx,
  mrtGetClampTextSx,
  mrtGetDateColumnSizing,
  mrtGetTableWrapperSx,
} from "../../utils/helpers";
import MRTActiveStatusCell from "./mrtActiveStatusCell";
import MRTDateTimeCell from "./mrtDateTimeCell";

const formatCellValue = value => {
  if (value == null) {
    return "";
  }

  if (value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (Array.isArray(value)) {
    return value
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    if (Object.keys(value).length === 0) {
      return "";
    }

    if (value.marker != null) {
      return [
        value.marker,
        value.value != null ? formatCellValue(value.value) : null,
        value.weight != null ? `weight: ${value.weight}` : null,
      ]
        .filter(Boolean)
        .join(" - ");
    }

    if (value.label || value.url) {
      return [value.label, value.url].filter(Boolean).join(" - ");
    }

    if (value.thumbnail || value.hero) {
      return [value.thumbnail, value.hero].filter(Boolean).join(", ");
    }

    if (value.vertical || value.campaignPriority != null) {
      return [value.vertical, value.campaignPriority]
        .filter(value => value != null && value !== "")
        .join(", ");
    }

    return Object.values(value)
      .map(item => formatCellValue(item))
      .filter(Boolean)
      .join(", ");
  }

  return value;
};

const getTextCellProps = theme => {
  return {
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const getCenteredCellProps = theme => {
  return {
    align: "center",
    sx: {
      whiteSpace: "normal",
      wordWrap: "break-word",
      borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
      padding: "4px 3px",
      verticalAlign: "top",
    },
  };
};

const renderTextCell = cell => {
  return (
    <Typography variant="body2" sx={mrtGetClampTextSx()}>
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

const renderCenteredTextCell = cell => {
  return (
    <Typography
      variant="subtitle1"
      sx={{
        textAlign: "center",
      }}
    >
      {formatCellValue(cell.getValue())}
    </Typography>
  );
};

// Large, easily readable Priority value (mirrors the big "Number" font used in
// the conversations table).
const renderPriorityCell = cell => {
  const value = formatCellValue(cell.getValue());

  return (
    <Typography
      variant="h5"
      sx={{
        textAlign: "center",
        fontSize: "1.75rem",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
  );
};

// Audience / Engagement metrics: numeric counts rendered in a larger,
// thousands-separated font so they read at a glance (mirrors the big number
// style used elsewhere in the portal tables).
const renderNumberCell = cell => {
  const rawValue = cell.getValue();

  if (rawValue == null || rawValue === "") {
    return null;
  }

  const numericValue = Number(rawValue);
  const displayValue = Number.isNaN(numericValue)
    ? formatCellValue(rawValue)
    : numericValue.toLocaleString();

  return (
    <Typography
      variant="h6"
      sx={{
        textAlign: "center",
        fontSize: "1.75rem",
        lineHeight: 1.1,
      }}
    >
      {displayValue}
    </Typography>
  );
};

// Lifecycle status for the "Active" column, derived from the archived flag and
// the campaign's date window:
//   archived                          -> Archived
//   not archived, before start date   -> Scheduled
//   not archived, within start/end    -> Active   (no end date = ongoing)
//   not archived, after end date      -> Ended
const getCampaignStatus = record => {
  if (record?.archived === true) {
    return "Archived";
  }

  const now = new Date();
  const start = record?.startDate ? new Date(record.startDate) : null;
  const end = record?.endDate ? new Date(record.endDate) : null;

  // The end date is inclusive: a campaign runs through the end of its end day,
  // so push the boundary to the last moment of that day before comparing.
  if (end && !Number.isNaN(end.getTime())) {
    end.setHours(23, 59, 59, 999);
  }

  if (start && !Number.isNaN(start.getTime()) && now < start) {
    return "Scheduled";
  }

  if (end && !Number.isNaN(end.getTime()) && now > end) {
    return "Ended";
  }

  return "Active";
};

const getStatusColor = (status, theme) => {
  switch (status) {
    case "Active":
      return theme.palette.success.main;
    case "Scheduled":
      return theme.palette.info.main;
    case "Ended":
      return theme.palette.grey[500];
    case "Archived":
    default:
      return theme.palette.error.main;
  }
};

const calculateColumns = theme => {
  return [
    {
      accessorKey: "startDate",
      header: "Start",
      ...mrtGetDateColumnSizing(),
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: MRTDateTimeCell,
    },
    {
      accessorKey: "endDate",
      header: "End",
      ...mrtGetDateColumnSizing(),
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: MRTDateTimeCell,
    },
    {
      // Derived campaign lifecycle status (Active / Scheduled / Ended / Archived)
      // rendered as the large colored chip. Kept separate from the raw "archived"
      // column below so the archived boolean can be filtered independently.
      id: "active",
      accessorFn: row => row.archived,
      header: "Status",
      size: 145,
      minSize: 135,
      maxSize: 170,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      // Sorting is disabled: the displayed status is computed from the date
      // window + archived flag, but manualSorting forwards the raw accessorKey
      // ("archived") to the backend, which can only sort by the boolean and
      // would ignore the Scheduled/Active/Ended distinction the column shows.
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ row }) => {
        const status = getCampaignStatus(row.original);
        return (
          <MRTActiveStatusCell label={status} backgroundColor={getStatusColor(status, theme)} />
        );
      },
    },
    {
      accessorKey: "priority",
      header: "Priority",
      size: 100,
      maxSize: 100,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderPriorityCell(cell),
    },
    {
      accessorKey: "title",
      header: "Title",
      size: 200,
      minSize: 120,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: false,
      // Title expands to fit its content on a single line (no word-wrap);
      // Summary below is the column that word-wraps.
      muiTableBodyCellProps: {
        sx: {
          borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          padding: "4px 3px",
          verticalAlign: "top",
        },
      },

      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "summary",
      header: "Summary",
      size: 220,
      minSize: 150,
      maxSize: 280,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: true,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "markers",
      header: "Markers",
      size: 180,
      minSize: 140,
      maxSize: 240,
      enableColumnFilterModes: false,
      // Sorting disabled in favor of a search/find filter on the header.
      enableColumnFilter: true,
      enableSorting: false,
      filterVariant: "text",
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "audience",
      header: "Audience",
      size: 110,
      minSize: 111,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderNumberCell(cell),
    },
    {
      accessorKey: "engagement",
      header: "Engaged",
      size: 120,
      minSize: 120,
      maxSize: 120,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderNumberCell(cell),
    },
    // ---- Columns below are hidden by default but kept available via the
    // ---- show/hide-columns button.
    {
      // Raw archived boolean, hidden by default. Exposed so users can filter
      // archived campaigns in/out (e.g. archived === true) independently of the
      // derived "Active" chip column above.
      accessorKey: "archived",
      header: "Archived",
      size: 90,
      maxSize: 110,
      enableColumnFilterModes: false,
      enableColumnFilter: true,
      filterVariant: "checkbox",
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 80,
      maxSize: 100,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: ({ cell }) => renderCenteredTextCell(cell),
    },
    {
      // The link the dialog now writes to the top-level `url` field (the old
      // `cta`/`metadata` objects were removed in the schema migration).
      accessorKey: "url",
      header: "Link",
      size: 120,
      maxSize: 160,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "assets",
      header: "Assets",
      size: 160,
      minSize: 120,
      maxSize: 220,
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      grow: true,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => renderTextCell(cell),
    },
    {
      accessorKey: "updatedAt",
      header: "Updated",
      size: 75,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: getCenteredCellProps(theme),
      Cell: MRTDateTimeCell,
    },
    {
      accessorKey: "id",
      header: "ID",
      size: 75,
      maxSize: 90,
      enableColumnFilterModes: false,
      enableColumnFilter: false,
      enableSorting: true,
      grow: false,
      muiTableBodyCellProps: getTextCellProps(theme),
      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ fontSize: "0.76rem" }}>
            {formatCellValue(cell.getValue())}
          </Typography>
        );
      },
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnOrder: [
      "mrt-row-actions",
      "startDate",
      "endDate",
      "active",
      "priority",
      "title",
      "summary",
      "markers",
      "audience",
      "engagement",
      "archived",
      "type",
      "url",
      "assets",
      "updatedAt",
      "id",
    ],
    columnVisibility: {
      id: false,
      updatedAt: false,
      type: false,
      active: true,
      // Hidden by default; users can reveal it to filter archived campaigns.
      archived: false,
      title: true,
      summary: true,
      startDate: true,
      endDate: true,
      url: false,
      assets: false,
      priority: true,
      markers: false,
      audience: true,
      engagement: true,
    },
  };
};

export const BotpressProductsTable = props => {
  const {
    data,
    onRequestNewPage = null,
    dataReduxPagination = null,
    totalItemCount = 0,
    onRefreshTable = null,
    onEditEntry = null,
  } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const columns = useMemo(() => calculateColumns(theme), [theme]);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(totalItemCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshIconSpinning, setIsRefreshIconSpinning] = useState(false);

  // Default ordering still comes from createdAt (newest first). The Date
  // column was removed from the UI, but in manualSorting mode this id is just
  // forwarded to the backend query, so it preserves the existing sort order.
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      // Pagination is client-side; don't let a data reload reset the page/size
      // the user picked. Only track the total for reference.
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  useEffect(() => {
    setIsLoading(false);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      if (!onRequestNewPage) {
        return;
      }

      if (!data.length) {
        setIsLoading(true);
      }

      try {
        let newQuery = {};

        columnFilters.forEach(element => {
          if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        return;
      }
    };

    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Pagination is client-side now, so changing page/size must NOT refetch —
    // only a filter or sort change reloads the full list from the server.
  }, [columnFilters, sorting]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: onEditEntry != null,
    enableRowSelection: false,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionActionsColumn: "first",
    rowCount,
    getRowId: row => row.id,
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      sorting,
      columnOrder,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    paginationDisplayMode: "pages",

    enableFirstPageButton: false,
    enableLastPageButton: false,

    // The saga loads every product into `data`, so pagination is handled
    // client-side here (MRT slices `data` into pages of pageSize). Sorting and
    // filtering stay server-side (manual) and refetch the full list.
    manualPagination: false,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        flexShrink: 0,
        minHeight: "48px",
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        flexShrink: 0,
        height: "56px",
        minHeight: "56px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        ...mrtGetActionColumnStyling(),
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            padding: "4px 6px",
            // In grid layout mode the body cell is a flex container, so the
            // action icon must be centered via justifyContent (text-align alone
            // won't center the flexed button).
            justifyContent: "center",
          },
        },
      },
    },
    icons: {
      MoreHorizIcon: () => <List color={"secondary"} sx={mrtGetActionIconSx(theme)} />,
    },
    renderRowActionMenuItems: ({ row, closeMenu }) =>
      [
        onEditEntry != null && (
          <MenuItem
            key="edit"
            onClick={() => {
              onEditEntry(row.original);
              closeMenu();
            }}
          >
            <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
            <ListItemText disableTypography={true}>
              <Typography variant="h5" color="textSecondary">
                Edit Campaign
              </Typography>
            </ListItemText>
          </MenuItem>
        ),
      ].filter(Boolean),
    renderTopToolbarCustomActions: () => <Box sx={{ minHeight: "40px" }} />,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {onRefreshTable && (
          <Tooltip title="Refresh campaigns" arrow>
            <IconButton
              onClick={() => {
                setIsRefreshIconSpinning(true);
                onRefreshTable();

                setTimeout(() => {
                  setIsRefreshIconSpinning(false);
                }, 800);
              }}
            >
              <Refresh
                sx={{
                  transformOrigin: "center",
                  animation: isRefreshIconSpinning ? "refreshSpin 0.8s linear 1" : "none",
                  "@keyframes refreshSpin": {
                    from: {
                      transform: "rotate(0deg)",
                    },
                    to: {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
  });

  return (
    <Box sx={mrtGetTableWrapperSx(0, !data?.length)}>
      <DatagridTooltipProvider>
        <MaterialReactTable table={table} />
      </DatagridTooltipProvider>
    </Box>
  );
};

export default BotpressProductsTable;
